import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Send, CheckCircle, AlertCircle, Edit3, X, Save,
  Pause, Play, Square, RefreshCw, Trash2,
} from 'lucide-react';
import { getCampaign, pauseCampaign, resumeCampaign, stopCampaign, deleteCampaign } from '../api/campaigns';
import { updateEmail, sendEmail, getEmailSettings, listRecipients } from '../api/email';

export default function CampaignDetail() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [sending, setSending] = useState({});
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [sendError, setSendError] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await getCampaign(campaignId);
      setCampaign(data);
      setEmails(data.emails || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh while generating
  useEffect(() => {
    if (campaign?.status !== 'generating') return;
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [campaign?.status, load]);

  const startEdit = (email) => {
    setEditingId(email.emailId);
    setEditForm({
      subject: email.subject,
      emailBody: email.body,
      recipientOverride: email.recipientOverride || email.prospectEmail || '',
    });
  };

  const saveEdit = async (emailId) => {
    setSaving(true);
    try {
      await updateEmail(emailId, editForm);
      setEmails(prev => prev.map(e =>
        e.emailId === emailId
          ? { ...e, subject: editForm.subject, body: editForm.emailBody, recipientOverride: editForm.recipientOverride }
          : e
      ));
      setEditingId(null);
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async (emailId) => {
    setSendError(null);
    const email = emails.find(e => e.emailId === emailId);
    const recipient = email?.recipientOverride || email?.prospectEmail;

    // Pre-flight checks
    try {
      const settings = await getEmailSettings();
      if (!settings?.senderEmail || settings.verificationStatus !== 'Success') {
        setSendError({
          title: 'Sender email not verified',
          message: 'You need to verify your sender email address before sending.',
          action: 'Go to Settings',
          link: '/settings',
        });
        return;
      }

      if (recipient) {
        const { recipients } = await listRecipients();
        const found = recipients?.find(r => r.email === recipient);
        if (!found || found.status !== 'Success') {
          setSendError({
            title: `Recipient not verified: ${recipient}`,
            message: 'During sandbox mode, recipients must be verified before you can send them emails. Add this email in Settings → Verified Recipients and ask them to click the confirmation link.',
            action: 'Go to Settings',
            link: '/settings',
          });
          return;
        }
      }
    } catch {
      // If pre-check fails, proceed anyway and let backend handle it
    }

    if (!window.confirm(`Send this email to ${recipient}?`)) return;
    setSending(prev => ({ ...prev, [emailId]: true }));
    try {
      const res = await sendEmail(emailId);
      setEmails(prev => prev.map(e =>
        e.emailId === emailId ? { ...e, status: 'sent', sentTo: res.sentTo } : e
      ));
    } catch (err) {
      const errMsg = err.message || '';
      if (errMsg.includes('not verified') || errMsg.includes('Email address is not verified')) {
        setSendError({
          title: 'Recipient email not verified',
          message: `The recipient "${recipient}" has not been verified in AWS SES. During sandbox mode, both sender and recipient must be verified. Go to Settings → Verified Recipients to add and verify this address.`,
          action: 'Go to Settings',
          link: '/settings',
        });
      } else if (errMsg.includes('not configured') || errMsg.includes('Settings first')) {
        setSendError({
          title: 'Email not configured',
          message: 'You haven\'t set up your sender email yet. Go to Settings to configure and verify your email address.',
          action: 'Go to Settings',
          link: '/settings',
        });
      } else {
        setSendError({
          title: 'Send failed',
          message: errMsg,
        });
      }
      setEmails(prev => prev.map(e =>
        e.emailId === emailId ? { ...e, status: 'failed', error: errMsg } : e
      ));
    } finally {
      setSending(prev => ({ ...prev, [emailId]: false }));
    }
  };

  const handleAction = async (action) => {
    setActionLoading(true);
    try {
      if (action === 'pause') await pauseCampaign(campaignId);
      else if (action === 'resume') await resumeCampaign(campaignId);
      else if (action === 'stop') await stopCampaign(campaignId);
      await load();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete campaign "${campaign?.name}"? This will remove all generated emails and cannot be undone.`)) return;
    setActionLoading(true);
    try {
      await deleteCampaign(campaignId);
      navigate('/campaigns');
    } catch (err) {
      alert('Delete failed: ' + err.message);
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="max-w-6xl mx-auto py-10 text-center text-slate-500">Campaign not found.</div>
    );
  }

  // Group emails by prospect
  const grouped = {};
  emails.forEach(e => {
    const key = e.prospectId || 'unknown';
    if (!grouped[key]) grouped[key] = { name: e.prospectName, email: e.prospectEmail, emails: [] };
    grouped[key].emails.push(e);
  });

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/campaigns')} className="text-slate-400 hover:text-slate-600">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">{campaign.name}</h1>
          <div className="flex items-center gap-4 mt-1">
            <span className="text-sm text-slate-500">Status: <span className="font-medium">{campaign.status}</span></span>
            <span className="text-sm text-slate-500">{campaign.stats?.generated || 0} sequences</span>
            <span className="text-sm text-slate-500">{campaign.stats?.sent || 0} sent</span>
          </div>
        </div>
        <div className="flex gap-2">
          {campaign.status === 'generating' && (
            <button onClick={load} className="p-2 text-slate-400 hover:text-slate-600">
              <RefreshCw className="w-5 h-5 animate-spin" />
            </button>
          )}
          {campaign.status === 'active' && (
            <button
              onClick={() => handleAction('pause')}
              disabled={actionLoading}
              className="px-3 py-1.5 bg-orange-100 text-orange-700 text-sm rounded-lg hover:bg-orange-200 flex items-center gap-1.5"
            >
              <Pause className="w-3.5 h-3.5" /> Pause
            </button>
          )}
          {campaign.status === 'paused' && (
            <button
              onClick={() => handleAction('resume')}
              disabled={actionLoading}
              className="px-3 py-1.5 bg-green-100 text-green-700 text-sm rounded-lg hover:bg-green-200 flex items-center gap-1.5"
            >
              <Play className="w-3.5 h-3.5" /> Resume
            </button>
          )}
          {(campaign.status === 'active' || campaign.status === 'generating') && (
            <button
              onClick={() => handleAction('stop')}
              disabled={actionLoading}
              className="px-3 py-1.5 bg-slate-100 text-slate-600 text-sm rounded-lg hover:bg-slate-200 flex items-center gap-1.5"
            >
              <Square className="w-3.5 h-3.5" /> Stop
            </button>
          )}
          <button
            onClick={handleDelete}
            disabled={actionLoading}
            className="px-3 py-1.5 bg-red-50 text-red-600 text-sm rounded-lg hover:bg-red-100 flex items-center gap-1.5"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </div>

      {campaign.status === 'generating' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 space-y-2">
          <div className="flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-yellow-600 animate-spin" />
            <p className="text-sm text-yellow-700 font-medium">
              Generating email sequences...
            </p>
          </div>
          <div className="flex items-center gap-4 ml-8">
            {campaign.stats?.total > 0 && (
              <>
                <div className="flex-1 bg-yellow-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-yellow-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(100, ((campaign.stats?.generated || 0) / campaign.stats.total) * 100)}%` }}
                  />
                </div>
                <span className="text-xs text-yellow-700 font-medium whitespace-nowrap">
                  {campaign.stats?.generated || 0} / {campaign.stats.total} prospects
                </span>
              </>
            )}
            {!campaign.stats?.total && (
              <span className="text-xs text-yellow-600">Loading prospects... Auto-refreshing every 5s.</span>
            )}
          </div>
        </div>
      )}

      {/* Send error banner */}
      {sendError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">{sendError.title}</p>
              <p className="text-sm text-red-600 mt-1">{sendError.message}</p>
              <div className="flex gap-3 mt-3">
                {sendError.link && (
                  <button
                    onClick={() => navigate(sendError.link)}
                    className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700"
                  >
                    {sendError.action}
                  </button>
                )}
                <button
                  onClick={() => setSendError(null)}
                  className="px-3 py-1.5 bg-red-100 text-red-700 text-xs font-medium rounded-lg hover:bg-red-200"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email list by prospect */}
      {Object.entries(grouped).map(([prospectId, group]) => (
        <div key={prospectId} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900">{group.name}</h3>
            <p className="text-xs text-slate-500">{group.email}</p>
          </div>

          <div className="divide-y divide-slate-100">
            {group.emails.map(email => (
              <div key={email.emailId} className="p-5">
                {editingId === email.emailId ? (
                  /* Edit mode */
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Recipient</label>
                      <input
                        value={editForm.recipientOverride}
                        onChange={(e) => setEditForm({ ...editForm, recipientOverride: e.target.value })}
                        className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Subject</label>
                      <input
                        value={editForm.subject}
                        onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                        className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Body</label>
                      <textarea
                        value={editForm.emailBody}
                        onChange={(e) => setEditForm({ ...editForm, emailBody: e.target.value })}
                        rows={6}
                        className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => saveEdit(email.emailId)}
                        disabled={saving}
                        className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1.5"
                      >
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        Save
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="px-3 py-1.5 bg-slate-100 text-slate-600 text-sm rounded-lg hover:bg-slate-200 flex items-center gap-1.5"
                      >
                        <X className="w-3.5 h-3.5" /> Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  /* View mode */
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-medium text-slate-400">
                          Step {email.stepNumber} {email.variant === 'b' ? '(B)' : '(A)'}
                        </span>
                        <span className="text-xs text-slate-400">Day {email.delayDays}</span>
                        {email.status === 'sent' && (
                          <span className="inline-flex items-center gap-1 text-xs text-green-600">
                            <CheckCircle className="w-3 h-3" /> Sent to {email.sentTo}
                          </span>
                        )}
                        {email.status === 'failed' && (
                          <span className="inline-flex items-center gap-1 text-xs text-red-600">
                            <AlertCircle className="w-3 h-3" /> Failed
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {(email.status === 'draft' || email.status === 'failed') && (
                          <>
                            <button
                              onClick={() => startEdit(email)}
                              className="p-1.5 text-slate-400 hover:text-blue-600 rounded"
                              title="Edit"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleSend(email.emailId)}
                              disabled={sending[email.emailId]}
                              className="px-3 py-1 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1.5"
                            >
                              {sending[email.emailId] ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Send className="w-3 h-3" />
                              )}
                              Send
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {email.recipientOverride && email.recipientOverride !== email.prospectEmail && (
                      <p className="text-xs text-orange-600 mb-1">To: {email.recipientOverride} (override)</p>
                    )}
                    <h4 className="font-medium text-slate-900 text-sm">{email.subject}</h4>
                    <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{email.body}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {emails.length === 0 && campaign.status !== 'generating' && (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <p className="text-slate-500">No emails generated yet.</p>
        </div>
      )}
    </div>
  );
}
